// @ts-check
import Fuse from 'fuse.js';
import BaseController from './BaseController';
import PointsDataSource from '../dataSources/PointsDataSource';
import ProjectDataSource from '../dataSources/ProjectDataSource';
import LocationAnalysisItem from '../objects/LocationAnalysisItem';
import UserPreferences from '../objects/UserPreferences';
import LocationAnalysisItemOrigin from '../enums/LocationAnalysisItemOrigin';
import { dealAreaFields, polygonParam } from '../helpers/Util';
import AppConfig from '../appConfig';
import { ALLOWED_POINT_TYPES_FOR_MY_PLACES_ANALYSIS } from '../enums/PointTypes';
import Errors from '../enums/Error';
import PointsFilterRule from '../objects/pointsFilter/PointsFilterRule';
import PointsFilter from '../objects/pointsFilter/PointsFilter';
import UserInfoDataSource from '../dataSources/UserInfoDataSource';
import { SELFSTORAGE_POINT_TYPE } from '../enums/SelfStoragePointType';

const SEARCH_THRESHOLD = 0.4;
const DOWNLOAD_LIMIT = 1000;

const SIZE_CATS = ['5x5', '5x10', '10x10', '10x15', '10x20', '10x30'];

class PointsController extends BaseController {
    static get name() {
        return 'PointsController';
    }

    static getInstance(options) {
        return new PointsController(options);
    }

    onActivate() {
        this.bindGluBusEvents({
            GEO_JSON_METADATA_LOAD_REQUEST: this.onGeoJsonMetadataLoadRequest,
            MAP_LOAD: this.onMapLoad,
            GEO_JSON_METADATA_REQUEST: this.onGeoJsonMetadataRequest,
            POINTS_POPUP_FIELDS_REQUEST: this.onPointPopupFieldsRequest,
            POINTS_FILTERS_REQUEST: this.onPointsFilterRequest,
            POINTS_SEARCH_FIELDS_REQUEST: this.onPointsSearchFieldsRequest,
            APPLY_POINTS_FILTERS_REQUEST: this.onApplyPointsFilter,
            SEARCH_POINT_GEOJSON_REQUEST: this.onGeoJsonSearch,
            FEATURE_TO_LOCATION_ANALYSIS: this.onFeatureToLocationAnalysis,
            UPDATE_GEO_JSON_LAYER_GROUP_VISIBILITY: this.onUpdateGeoJsonLayerGroupVisibility,
            UPDATE_GEO_JSON_LAYER_VISIBILITY: this.onUpdateGeoJsonLayerVisibility,
            CLEAR_FACILITY_FILTER: this.onClearFacilityFilter,
            CLEAR_FACILITY_FILTER_FROM_PANEL: this.onClearFacilityFilter,
            START_LOCATION_ANALYSIS: this.onStartLocationAnalysis,
            MAP_CURRENT_FILTERS_TO_CUSTOMIZATION_DETAILS:
                this.mapCurrentFiltersToCustomizationDetails,
            GET_STORAGE_FACILITY_COUNT_REQUEST: this.onGetStorageFacilityCount,
        });
        this.pointsDataSource = this.activateSource(PointsDataSource);
        this.projectDataSource = this.activateSource(ProjectDataSource);
        this.userInfoDataSource = this.activateSource(UserInfoDataSource);
        this.pointsDataSource.loadPointsMetadata();
        this.searchTerm = '';
        this.addedCriteria = [];
        this.appliedFilters = {};
        this.filterCombinerType = null;
        this.customizationDetailsForStorageReport = {};
    }

    onMapLoad = async () => {
        try {
            await this.pointsDataSource.loadPointsGeoJson();
            const geoJson = this.pointsDataSource.pointsGeoJson;
            if (!geoJson) return;
            this.bus.emit('POINTS_GEOJSON', geoJson);

            // Initialize fuzzy search
            const fields4search = this.searchPropertiesToFields().filter(
                filter => filter.weight > 0,
            );
            this.fuse = new Fuse(geoJson.features, {
                includeScore: true, // score 0 perfect, 1 no match at all
                isCaseSensitive: false,
                shouldSort: true, // sorted by score ascending
                threshold: SEARCH_THRESHOLD, // closer to 0, stronger match
                findAllMatches: true, // this slows down the search
                ignoreLocation: true, // ignore where in the string the pattern appears
                ignoreFieldNorm: true,
                minMatchCharLength: 3, // higher number, better match, less results
                keys: fields4search.map(({ property, weight }) => ({
                    // https://fusejs.io/examples.html#weighted-search
                    name: `properties.${property}`, // due to geo feature structure
                    weight,
                })),
            });
            this._dataList = geoJson.features;
        } catch (e) {
            console.error(e);
        }
    };

    onGeoJsonMetadataLoadRequest = async () => {
        await this.pointsDataSource.loadPointsMetadata();
        const metadata = this.pointsDataSource.pointsMetadata;
        this.bus.emit(
            metadata ? 'GEO_JSON_METADATA_LOAD_SUCCESS' : 'GEO_JSON_METADATA_LOAD_ERROR',
            metadata,
        );
    };

    onPointPopupFieldsRequest() {
        const popupFields = this.popupPropertiesToFields();
        this.bus.emit('POINTS_POPUP_FIELDS', { popupFields });
    }

    onGeoJsonMetadataRequest(geoJsonMetadataRequest) {
        this.bus.emit(
            'GEO_JSON_METADATA',
            this.pointsDataSource.pointsMetadata,
            geoJsonMetadataRequest.source,
        );
    }

    onUpdateGeoJsonLayerGroupVisibility(e) {
        const { visible, mapInstanceId } = e;
        this.bus.emit('MAP_APPLY_GEO_JSON_LIBRARY_LAYERS_UPDATE', { mapInstanceId, visible });
    }

    onUpdateGeoJsonLayerVisibility(e) {
        const { layerVisibility, featurePropertyNameEquivalentToLayerId } = e;
        this.bus.emit('MAP_APPLY_GEO_JSON_LAYER_UPDATE', {
            layerVisibility,
            featurePropertyNameEquivalentToLayerId,
        });
    }

    getLocationType = type => {
        switch (type) {
            // SelfStorage specific types
            case SELFSTORAGE_POINT_TYPE.SS_FACILITY:
                return 'SELFSTORAGE_FACILITY';
            case SELFSTORAGE_POINT_TYPE.SS_CONSTRUCTION:
                return 'SELFSTORAGE_CONSTRUCTION';
            // Unknown type, default to custom point
            default:
                return 'CUSTOM_POINT';
        }
    };

    getIconForPointType = type => {
        switch (type) {
            case 'SELFSTORAGE_FACILITY':
                return 'warehouse';
            case 'SELFSTORAGE_CONSTRUCTION':
                return 'front_loader';
            default:
                return 'place';
        }
    };

    /**
     * @param {object} param0
     * @param {import('../types').VisualReport} param0.visualReport
     * @param {string} param0.mapInstanceId
     * @description Prepares feature for analysis if it is a POI. Else, proceed straight to analysis
     */
    onStartLocationAnalysis = ({ visualReport, mapInstanceId }) => {
        const facilityType = visualReport.pointType;
        const newLocationAnalysisItem = new LocationAnalysisItem({
            type: facilityType,
            id: visualReport.facilityId,
            value: visualReport.title,
            point: visualReport.point,
            itemOrigin: LocationAnalysisItemOrigin.USER_SAVED_LOCATION,
            ...dealAreaFields(visualReport),
            dealId: visualReport.id,
            listId: visualReport.listId,
            status: visualReport.status,
            notes: visualReport.notes,
            dealListName: visualReport.dealListName,
        });

        const report = ALLOWED_POINT_TYPES_FOR_MY_PLACES_ANALYSIS.some(pt => pt === facilityType)
            ? 'selfstorage-point-info'
            : AppConfig.constants.defaultInsightsReport;

        const visualReportProps = {
            report: report,
            pointType: facilityType,
            facilityId: visualReport.facilityId,
        };

        this.bus.emit('ENTER_UPDATE_LOCATION_ANALYSIS_MODE', {
            selectedItem: newLocationAnalysisItem,
            mapInstanceId,
            showInsights: true,
            visualReportProps,
        });
    };

    /**
     * @param {object} param0
     * @param {import('@turf/helpers').Feature} param0.feature
     * @param {string} param0.mapInstanceId
     * @param {object} param0.visualReportProps
     */
    onFeatureToLocationAnalysis = ({ feature, mapInstanceId, visualReportProps }) => {
        this.bus.emit('ENTER_UPDATE_LOCATION_ANALYSIS_MODE', {
            mapInstanceId:
                mapInstanceId == null
                    ? this.projectDataSource.defaultMapInstance.id
                    : mapInstanceId,
            selectedItem: this.mapFeatureToLocationAnalysisItem(feature),
            showInsights: AppConfig.constants.searchBox.shouldShowInsightsOnPointSearch,
            visualReportProps: visualReportProps,
        });
    };

    mapPointTypeToValidVisualReportParam = type => {
        switch (type) {
            case 'nopricing':
            case 'pricing':
                return SELFSTORAGE_POINT_TYPE.SS_FACILITY;
            case 'construction':
                return SELFSTORAGE_POINT_TYPE.SS_CONSTRUCTION;
            case 'hotel':
                return 'hotel';
            case 'convention_center':
                return 'convention_center';
            case 'construction_site':
                return 'construction_site';
            default:
                return 'place';
        }
    };

    /**
     * Transforms a given point feature into a location analysis item
     * @param {import('@turf/helpers').Feature} feature
     * @param {LocationAnalysisItemOrigin} [locationAnalysisOrigin=LocationAnalysisItemOrigin.GEO_JSON]
     * @returns {LocationAnalysisItem}
     */
    mapFeatureToLocationAnalysisItem = (
        feature,
        locationAnalysisOrigin = LocationAnalysisItemOrigin.GEO_JSON,
    ) => {
        // Transform the point feature into a location analysis item
        const { idProperty, mainProperties } =
            this.pointsDataSource.pointsMetadata.pointPreviewProperties;
        const name = mainProperties.map(property => feature.properties[property]).join(', ');
        const pointTypeProperty =
            this.pointsDataSource.pointsMetadata.mapProperties.legend
                .featurePropertyNameEquivalentToLayerId;

        const { analysisTypeId, selection } = UserPreferences.toLocationAnalysisFormat(
            this.userInfoDataSource.userPreferences,
        );

        return new LocationAnalysisItem({
            id: feature.properties[idProperty],
            type: this.mapPointTypeToValidVisualReportParam(feature.properties[pointTypeProperty]),
            value: name,
            point: {
                lng: feature.geometry.coordinates[0],
                lat: feature.geometry.coordinates[1],
            },
            itemOrigin: locationAnalysisOrigin,
            // add default selection and analysis type
            analysisTypeId,
            feature,
            selection,
            searchBoxOrigin: 'POINT',
            icon: this.getIconForPointType(this.getLocationType(pointTypeProperty)),
        });
    };

    onPointsFilterRequest = async () => {
        if (!this.pointsDataSource.pointsGeoJson) return;
        this.bus.emit(
            'POINTS_FILTERS_RESPONSE',
            this.filterPropertiesToFields(),
            this.addedCriteria,
            this.appliedFilters,
            this.filterCombinerType,
        );
    };

    onPointsSearchFieldsRequest = () => {
        this.bus.emit('POINTS_SEARCH_FIELDS_RESPONSE', this.searchPropertiesToFields());
    };

    /**
     *
     * @param {object} payload
     * @param {string} payload.filterCombinerType
     * @param {import('../').AppliedPointFilters} payload.appliedFilters
     * @param {import('../').PointsFilterField[]} payload.addedCriteria
     */
    onApplyPointsFilter = ({ filterCombinerType, appliedFilters, addedCriteria }) => {
        this.addedCriteria = addedCriteria;
        this.filterCombinerType = filterCombinerType;
        this.appliedFilters = appliedFilters || {};
        this.bus.emit('UPDATE_APPLIED_FILTERS_REQUEST', {
            appliedGeoFilters: {
                appliedFilters: this.appliedFilters,
            },
        });
        this.bus.emit('MAP_APPLY_GEO_JSON_LAYER_UPDATE', {
            filterCombinerType,
            appliedFilters,
        });
    };

    onClearFacilityFilter = ({ mapInstanceId }) => {
        this.addedCriteria = [];
        this.appliedFilters = {};
        this.filterCombinerType = null;
        this.bus.emit('MAP_APPLY_GEO_JSON_LAYER_UPDATE', {
            filterCombinerType: undefined,
            appliedFilters: undefined,
        });
        this.bus.emit('MAP_CURRENT_FILTERS_TO_CUSTOMIZATION_DETAILS', {
            mapInstanceId: mapInstanceId,
        });
    };

    /**
     *
     * @param {object} payload
     * @param {string} payload.searchTerm
     * @param {number} payload.maxResults
     */
    onGeoJsonSearch = ({ searchTerm, maxResults = 10 }) => {
        // Gets the result sorted by score
        // Search config is part of onMapLoad() implementation
        // If the user typed a numeric facility_id, do an exact match (ignore leading zeros)
        const FACILITY_ID_REGEX = /^\d+$/;
        if (FACILITY_ID_REGEX.test(searchTerm)) {
            const normalized = searchTerm.replace(/^0+/, '');
            const exact = this._dataList
                .filter(
                    feature =>
                        feature.properties.facility_id &&
                        feature.properties.facility_id.replace(/^0+/, '') === normalized,
                )
                .map(feature => ({
                    ...feature,
                    properties: {
                        ...feature.properties,
                        foundByFacilityIdMatch: true,
                    },
                }));
            this.bus.emit('SEARCH_POINT_GEOJSON_RESPONSE', {
                count: exact.length,
                results: exact.slice(0, maxResults),
            });
            return;
        }

        // Otherwise do the normal fuzzy search
        const results = this.fuse.search(searchTerm);
        this.bus.emit('SEARCH_POINT_GEOJSON_RESPONSE', {
            count: results.length,
            results: results.slice(0, maxResults).map(r => ({
                ...r.item,
                properties: {
                    ...r.item.properties,
                    foundByFacilityIdMatch: false,
                },
            })),
        });
    };

    mapCurrentFiltersToCustomizationDetails = async ({ mapInstanceId }) => {
        this.bus.emit('POINTS_FILTERS_REQUEST');

        const facilityCount = await this.onGetStorageFacilityCount(mapInstanceId);

        const areFiltersApplied = this.addedCriteria.length > 0 ? this.filterCombinerType : '';
        this.customizationDetailsForStorageReport = {
            matchType: areFiltersApplied,
            criterias: areFiltersApplied
                ? this.addedCriteria.map(item => {
                      return {
                          label: item.label,
                          dropdownValue: item.type === 'number' ? item.filterType : item.value,
                          inputValue: item.type === 'number' ? item.value : '',
                      };
                  })
                : [],
        };
        this.bus.emit('GET_CURRENT_CUSTOMIZATION_DETAILS', {
            customizationDetails: this.customizationDetailsForStorageReport,
            facilityCount,
        });
    };

    onGetStorageFacilityCount = async ({ mapInstanceId }) => {
        try {
            const mapInstance = this.projectDataSource.getActiveMapInstance(mapInstanceId);
            if (!mapInstance) {
                return;
            }
            const { locationAnalysisItem } = mapInstance;

            if (!locationAnalysisItem) {
                this.bus.emit('GET_STORAGE_FACILITY_COUNT_SUCCESS', { facilityCount: 0 });
                return;
            }

            const { analysisType } = locationAnalysisItem;

            const selections = locationAnalysisItem.sortedSelectionAsArray || [];
            const largestSelection = selections.length ? selections[selections.length - 1] : null;

            if (largestSelection == null) {
                this.bus.emit('GET_STORAGE_FACILITY_COUNT_SUCCESS', { facilityCount: 0 });
                return;
            }

            const payload = {
                site: {
                    type: 'custom',
                    lng: locationAnalysisItem.point.lng,
                    lat: locationAnalysisItem.point.lat,
                },
                reportContours: {
                    type: analysisType.VISUAL_REPORT_PROFILE,
                    values: [largestSelection],
                    polygon: locationAnalysisItem.isPolygon
                        ? polygonParam(locationAnalysisItem.ring)
                        : undefined,
                },
            };

            const section = await this.pointsDataSource.fetchOlapSiteAnalysisForSelection(
                payload,
                'selfstorage-facility-with-pricing',
                largestSelection,
            );

            const rawFacilities = section && Array.isArray(section.data) ? section.data : [];

            const hasAppliedFilters =
                this.appliedFilters && Object.keys(this.appliedFilters).length > 0;

            const facilities = hasAppliedFilters
                ? this.filterFacilities(rawFacilities)
                : rawFacilities;

            this.bus.emit('GET_STORAGE_FACILITY_COUNT_SUCCESS', {
                facilityCount: facilities.length,
            });

            return facilities.length;
        } catch (e) {
            console.error(e);
            this.bus.emit('GET_STORAGE_FACILITY_COUNT_SUCCESS', { facilityCount: 0 });
        }
    };

    /**
     * Given an array of raw facility objects, apply the current
     * this.appliedFilters + this.filterCombinerType and return
     * the filtered array of facility properties.
     *
     * @param {Array<object>} rawFacilities
     * @returns {Array<object>}
     */
    filterFacilities(rawFacilities) {
        const rules = Object.keys(this.appliedFilters).map(
            prop =>
                new PointsFilterRule({
                    property: prop.replace(/[@][0-9]+/g, ''),
                    value: this.appliedFilters[prop].value,
                    filterType: this.appliedFilters[prop].filterType,
                }),
        );

        const pointsFilter = new PointsFilter({
            filterCombinerType: this.filterCombinerType,
            filterRules: rules,
        });

        return pointsFilter
            .filter(rawFacilities.map(f => ({ type: 'Feature', properties: f })))
            .map(feat => feat.properties);
    }

    // Properties to fields mapping
    searchPropertiesToFields = () => {
        const metadata = this.pointsDataSource.pointsMetadata;
        return metadata.searchProperties.map(property => ({
            property,
            label: metadata.properties[property].label,
            weight: metadata.properties[property].searchWeight,
            isTitle: metadata.properties[property].isTitle,
        }));
    };

    fieldFromPropertyId = (propertyId, metadata, isIndented) => ({
        property: propertyId,
        label: metadata.properties[propertyId].label,
        isTitle: metadata.properties[propertyId].isTitle,
        isHighlighted: metadata.properties[propertyId].isHighlighted,
        isWebsite: metadata.properties[propertyId].isWebsite,
        isIndented,
        format: metadata.properties[propertyId].format,
    });

    popupPropertiesToFields = () => {
        const metadata = this.pointsDataSource.pointsMetadata;

        if (!metadata || !metadata.popupProperties) {
            this.bus.emit('POPUP_PROPERTIES_LOAD_ERROR', {
                level: Errors.CRITICAL,
                originalError: 'Popup properties error',
                additionalInfo: 'Points metadata not loaded yet.',
            });
            return;
        }

        const popupProperties = metadata.popupProperties;

        const result = {
            sameForAllPointTypes: popupProperties.sameForAllPointTypes,
            typeProperty: popupProperties.typeProperty,
            propertiesForPointType: {},
        };

        Object.keys(popupProperties.propertiesForPointType).forEach(pointType => {
            result.propertiesForPointType[pointType] = [];
            popupProperties.propertiesForPointType[pointType].forEach(property => {
                if (Array.isArray(property)) {
                    property.forEach(item => {
                        result.propertiesForPointType[pointType].push(
                            this.fieldFromPropertyId(item, metadata, true),
                        );
                    });
                } else {
                    result.propertiesForPointType[pointType].push(
                        this.fieldFromPropertyId(property, metadata, false),
                    );
                }
            });
        });

        return result;
    };

    filterPropertiesToFields = () => {
        const propertyValues = {};
        // Get all feature properties and values
        const geoJson = this.pointsDataSource.pointsGeoJson;
        geoJson.features.forEach(feature => {
            if (feature.properties) {
                Object.keys(feature.properties).forEach(property => {
                    if (propertyValues[property] == null) {
                        propertyValues[property] = [];
                    }
                    if (feature.properties[property] != null) {
                        propertyValues[property].push(feature.properties[property]);
                    }
                });
            }
        });
        const metadata = this.pointsDataSource.pointsMetadata;
        return metadata.filterProperties.map(property => {
            /** @type {import('../').PointsFilterField} */
            const filter = {
                property,
                type: metadata.properties[property].type,
                label: metadata.properties[property].label,
                prefix: metadata.properties[property].filterPrefix,
                suffix: metadata.properties[property].filterSuffix,
                filterType: metadata.properties[property].defaultFilterType,
                value: metadata.properties[property].defaultFilterValue,
            };
            const values = Array.from(new Set(propertyValues[property]));
            if (filter.type === 'string') {
                filter.values = values.filter(value => {
                    // Exclude empty strings for the two company-ish properties
                    // (almost every facility has no contractor recorded, so a
                    // blank option would match nearly all of them) and values
                    // with multiple types for 'facility_type'
                    return (
                        !(
                            (filter.property === 'management_type' ||
                                filter.property === 'general_contractor_company_name') &&
                            value === ''
                        ) && !(filter.property === 'facility_type' && value.includes(','))
                    );
                });
            } else if (filter.type === 'number') {
                filter.min = Math.min(...values);
                filter.max = Math.max(...values);
            } else if (filter.type === 'boolean') {
                filter.values = [true, false];
            }
            return filter;
        });
    };

    onDeactivate() {
        this.unbindGluBusEvents();
    }
}

export default PointsController;
